import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { 
    FiShoppingBag, 
    FiClock,
    FiPackage,
    FiTag, 
    FiTruck,
    FiImage,
    FiMessageSquare,
    FiSliders,
    FiUsers,
    FiSmartphone,
    FiMail,
    FiBarChart2,
    FiPhoneCall,
    FiChevronDown,
    FiChevronRight,
} from "react-icons/fi";
import Header from "../components/header/Header";

interface MenuChild {
    name: string;
    link: string;
}

interface MenuItem {
    name: string;
    link?: string;
    icon: React.ReactNode;
    children?: MenuChild[];
}

const menuItems: MenuItem[] = [
    {
        name: "Pending Orders",
        link: "/",
        icon: <FiClock size={18} />
    },
    {   
        name: "Order Details",
        link: "/orders",
        icon: <FiShoppingBag size={18} />
    },
    {
        name: "Products",
        link: "/products",
        icon: <FiPackage size={18} />
    },
    {
        name: "Categories",
        link: "/categories",
        icon: <FiTag size={18} />
    },
    {
        name: "Vendors",
        link: "/vendors",
        icon: <FiTruck size={18} />
    },
    {
        name: "Images",
        link: "/images",
        icon: <FiImage size={18} />
    },
    {
        name: "Chat Flows",
        link: "/chatflows",
        icon: <FiMessageSquare size={18} />
    },
    {
        name: "Chat Branding",
        link: "/chatbranding",
        icon: <FiSliders size={18} />
    },
    {
        name: "Member Listing",
        link: "/organizations",
        icon: <FiUsers size={18} />
    },
    {
        name: "Send SMS",
        link: "/sms",
        icon: <FiSmartphone size={18} />
    },
    {
        name: "Email Templates",
        link: "/email-templates",
        icon: <FiMail size={18} />
    },
    {
        name: "CSM Follow-ups",
        link: "/csm",
        icon: <FiPhoneCall size={18} />
    },
    {
        name: "Reports",
        icon: <FiBarChart2 size={18} />,
        children: [
            {
                name: "User Report",
                link: "/reports/users",
            },
            {
                name: "Twilio Report",
                link: "/reports/twilio",
            },
        ],
    },
]

const VerticalLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen] = useState(true);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
            navigate("/login");
        }
    }, [navigate]);

    useEffect(() => {
        const next: Record<string, boolean> = {};
        menuItems.forEach((item) => {
            if (item.children?.some((child) => isChildActive(child.link, location.pathname))) {
                next[item.name] = true;
            }
        });
        setOpenGroups((prev) => ({ ...prev, ...next }));
    }, [location.pathname]);

    const isActive = (path: string) => {
        if (path === "/") {
            return location.pathname === "/";
        }
        return location.pathname === path || location.pathname.startsWith(path + "/");
    };

    const isChildActive = (path: string, pathname: string) =>
        pathname === path || pathname.startsWith(path + "/");

    const isGroupActive = (item: MenuItem) =>
        Boolean(item.children?.some((child) => isChildActive(child.link, location.pathname)));

    const toggleGroup = (name: string) => {
        setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const linkClass = (active: boolean) =>
        `flex items-center gap-2.5 px-2.5 py-2.5 text-sm rounded-lg transition-all ${
            active
                ? "text-[#dbb685] font-semibold bg-[#dbb685]/10 shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
        }`;

    return <>
        <div className="flex w-screen h-screen bg-gray-50 overflow-hidden">
            {/* Left Sidebar */}
            <div className={`${sidebarOpen ? 'w-56' : 'w-0'} h-screen bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out overflow-hidden`}>
                {/* Logo Section */}
                <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-center">
                    {sidebarOpen && (
                        <img 
                            src="https://memorique.ai/wp-content/uploads/2025/05/No-background-Memorique.svg" 
                            alt="Memorique Logo" 
                            className="h-7 w-auto"
                        />
                    )}
                </div>

                {/* Navigation Menu */}
                <div className="flex-1 px-3 py-6 overflow-y-auto">
                    <nav className="space-y-1">
                        {menuItems.map((menuItem) => {
                            if (menuItem.children?.length) {
                                const groupOpen = Boolean(openGroups[menuItem.name]);
                                const groupActive = isGroupActive(menuItem);
                                return (
                                    <div key={menuItem.name}>
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(menuItem.name)}
                                            className={`${linkClass(groupActive)} w-full`}
                                        >
                                            <span className={groupActive ? "text-[#dbb685]" : "text-gray-500"}>
                                                {menuItem.icon}
                                            </span>
                                            {sidebarOpen && (
                                                <>
                                                    <span className="flex-1 text-left">{menuItem.name}</span>
                                                    <span className="text-gray-400">
                                                        {groupOpen ? (
                                                            <FiChevronDown size={14} />
                                                        ) : (
                                                            <FiChevronRight size={14} />
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                        {groupOpen && sidebarOpen && (
                                            <div className="mt-0.5 ml-4 space-y-0.5 border-l border-gray-200 pl-2">
                                                {menuItem.children.map((child) => (
                                                    <Link
                                                        key={child.link}
                                                        to={child.link}
                                                        className={linkClass(isActive(child.link))}
                                                    >
                                                        {child.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                            <Link
                                key={menuItem.link}
                                to={menuItem.link!}
                                className={linkClass(isActive(menuItem.link!))}
                            >
                                <span className={`${isActive(menuItem.link!) ? 'text-[#dbb685]' : 'text-gray-500'}`}>
                                    {menuItem.icon}
                                </span>
                                {sidebarOpen && <span>{menuItem.name}</span>}
                            </Link>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <Header />
                
                {/* Content */}
                <div className="flex-1 overflow-auto bg-gray-50">
                    <Outlet />
                </div>
            </div>
        </div>
    </>
}

export default VerticalLayout;
